import { z } from 'zod'

export const ANALYTICS_EVENT_NAMES = [
  'mist_landing_viewed',
  'mist_waitlist_started',
  'mist_waitlist_submitted',
  'mist_verification_sent',
  'mist_email_verified',
  'mist_giveaway_started',
  'mist_eligibility_checked',
  'mist_giveaway_entered',
  'mist_referral_verified',
  'mist_reveal_milestone',
  'mist_3d_capability',
  'mist_3d_asset_ready',
  'mist_3d_fallback',
  'mist_archive_article_viewed',
  'mist_shopify_buy_clicked',
  'mist_checkout_started',
  'mist_purchase_completed',
  'mist_api_error',
  'mist_collection_interaction',
] as const

export const AnalyticsEventNameSchema = z.enum(ANALYTICS_EVENT_NAMES)
export type AnalyticsEventName = z.infer<typeof AnalyticsEventNameSchema>

const ATTRIBUTION_PROPERTIES = [
  'route',
  'campaign',
  'referrer_domain',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'first_touch_id',
  'current_touch_id',
  'referral_campaign_id',
  'approved_ad_id',
] as const

export const ANALYTICS_EVENT_PROPERTY_ALLOWLIST: Record<AnalyticsEventName, readonly string[]> = {
  mist_landing_viewed: ATTRIBUTION_PROPERTIES,
  mist_waitlist_started: ATTRIBUTION_PROPERTIES,
  mist_waitlist_submitted: [...ATTRIBUTION_PROPERTIES, 'consent_launch_email', 'consent_marketing', 'consent_policy_version'],
  mist_verification_sent: ['request_id', 'route'],
  mist_email_verified: ['route'],
  mist_giveaway_started: ['route'],
  mist_eligibility_checked: ['route', 'eligibility_state'],
  mist_giveaway_entered: ['route', 'consent_giveaway_entry', 'consent_marketing', 'consent_policy_version'],
  mist_referral_verified: ['route', 'referral_campaign_id'],
  mist_reveal_milestone: ['route', 'reveal_milestone', 'capability_tier'],
  mist_3d_capability: ['route', 'capability_tier'],
  mist_3d_asset_ready: ['route', 'capability_tier', 'asset_class', 'timing_bucket'],
  mist_3d_fallback: ['route', 'capability_tier', 'fallback_reason'],
  mist_archive_article_viewed: [...ATTRIBUTION_PROPERTIES, 'archive_article_slug'],
  mist_shopify_buy_clicked: ['route', 'checkout_status', 'unattributed', 'first_touch_id', 'current_touch_id', 'referral_campaign_id'],
  mist_checkout_started: ['route', 'checkout_status', 'unattributed', 'first_touch_id', 'current_touch_id', 'referral_campaign_id'],
  mist_purchase_completed: ['route', 'currency', 'amount_cents', 'purchase_status', 'unattributed', 'first_touch_id', 'current_touch_id', 'referral_campaign_id', 'campaign'],
  mist_api_error: ['request_id', 'route', 'error_code', 'retryable', 'field_error_codes'],
  mist_collection_interaction: ['route', 'collection_action', 'collection_month', 'collection_line', 'edition_id'],
}

const EMAIL_VALUE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i
const SECRET_VALUE = /(?:secret-token|token-secret|shpat_|phc_[A-Za-z0-9]{12,})/i
const SENSITIVE_PROPERTY_KEY = /email|token|link|attestation|fraud|address|winner|secret|api.?key|free.?text|raw.?text|order.?id|payment/i
const SENSITIVE_URL_VALUE = /https?:\/\/\S*(?:token=|\/verify#|attestation|address|winner|email=)/i
const SUBSCRIBER_PUBLIC_ID = /^sub_[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const SAFE_ANALYTICS_PROPERTY_KEYS = Object.fromEntries(
  Object.values(ANALYTICS_EVENT_PROPERTY_ALLOWLIST).flat().map((key) => [key, true]),
) as Record<string, true>

export const AnalyticsPropertiesSchema = z.record(z.string(), z.unknown()).superRefine((properties, context) => {
  for (const [key, value] of Object.entries(properties)) {
    if (isSensitivePropertyKey(key) || containsSensitiveValue(value) || !isJsonPrimitiveValue(value)) {
      context.addIssue({ code: 'custom', path: [key], message: 'PROPERTY_NOT_ALLOWED' })
    }
  }
})

export const AnalyticsEventSchema = z.object({
  eventName: AnalyticsEventNameSchema,
  distinctId: z.string().min(1).max(200).refine((value) => !containsSensitiveValue(value), 'DISTINCT_ID_NOT_PRIVACY_SAFE'),
  subscriberPseudoId: z.string().min(1).max(80).regex(SUBSCRIBER_PUBLIC_ID).optional(),
  properties: AnalyticsPropertiesSchema,
  idempotencyKey: z.string().min(1).max(200).optional(),
}).superRefine((event, context) => {
  const allowed = ANALYTICS_EVENT_PROPERTY_ALLOWLIST[event.eventName]
  for (const [key, value] of Object.entries(event.properties)) {
    if (!allowed.includes(key) || isSensitivePropertyKey(key) || containsSensitiveValue(value) || !isJsonPrimitiveValue(value)) {
      context.addIssue({ code: 'custom', path: ['properties', key], message: 'PROPERTY_NOT_ALLOWED' })
    }
  }
})

export type AnalyticsProperties = z.infer<typeof AnalyticsPropertiesSchema>
export type AnalyticsEvent = z.infer<typeof AnalyticsEventSchema>

export function sanitizeEvent(eventName: AnalyticsEventName | string, properties: Record<string, unknown> = {}): AnalyticsProperties {
  const parsedName = AnalyticsEventNameSchema.safeParse(eventName)
  if (!parsedName.success) throw new Error('EVENT_NOT_ALLOWED')
  const allowed = ANALYTICS_EVENT_PROPERTY_ALLOWLIST[parsedName.data]
  const sanitized: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(properties)) {
    if (!allowed.includes(key) || isSensitivePropertyKey(key) || containsSensitiveValue(value) || !isJsonPrimitiveValue(value)) {
      throw new Error('PROPERTY_NOT_ALLOWED')
    }
    sanitized[key] = value
  }

  return AnalyticsPropertiesSchema.parse(sanitized)
}

export function isSubscriberPublicId(value: string): boolean {
  return SUBSCRIBER_PUBLIC_ID.test(value)
}

const FunnelStepSchema = z.object({
  event: AnalyticsEventNameSchema,
  filter: z.record(z.string(), z.string()).optional(),
})

const DashboardSchema = z.object({
  id: z.enum([
    'landing_verified_giveaway',
    'referral_visit_credit',
    'archive_product_checkout_purchase',
    'three_d_tier_conversion',
    'channel_verified_purchase_revenue',
  ]),
  title: z.string().min(1),
  description: z.string().min(1),
  funnel: z.object({ steps: z.array(FunnelStepSchema).min(2) }),
  breakdowns: z.array(z.string()).default([]),
  metrics: z.array(z.string()).default([]),
})

const AlertSchema = z.object({
  id: z.enum([
    'api_errors',
    'verification_send_failures',
    'conversion_drop',
    'fraud_backlog',
    'draw_audit_anomaly',
    'core_web_vitals_regression',
  ]),
  kind: z.enum([
    'api_error_rate',
    'verification_delivery_failure',
    'conversion_drop',
    'fraud_review_backlog',
    'draw_audit_anomaly',
    'core_web_vitals',
  ]),
  name: z.string().min(1),
  query: z.string().min(1),
  threshold: z.object({ operator: z.enum(['>', '<', '>=', '<=']), value: z.number() }),
  window: z.string().min(1),
})

const FeatureFlagSchema = z.object({
  key: z.enum(['mist_cta_copy', 'mist_static_vs_3d']),
  description: z.string().min(1),
})

export const PostHogResourceManifestSchema = z.object({
  version: z.literal(1),
  project: z.object({
    name: z.literal('MIST by BDQ'),
    hostEnv: z.literal('POSTHOG_HOST'),
    projectTokenEnv: z.literal('NEXT_PUBLIC_POSTHOG_KEY'),
    serverTokenEnv: z.literal('POSTHOG_API_KEY'),
    projectIdEnv: z.literal('POSTHOG_PROJECT_ID'),
  }),
  sourceMaps: z.object({
    enabled: z.literal(true),
    app: z.literal('apps/web'),
    buildDirectory: z.literal('apps/web/.next'),
    authTokenEnv: z.literal('POSTHOG_AUTH_TOKEN'),
    projectIdEnv: z.literal('POSTHOG_PROJECT_ID'),
  }),
  dashboards: z.array(DashboardSchema).length(5),
  alerts: z.array(AlertSchema).length(6),
  featureFlags: z.array(FeatureFlagSchema).length(2),
}).superRefine((manifest, context) => {
  const serialized = JSON.stringify(manifest)
  if (EMAIL_VALUE.test(serialized) || /phc_[A-Za-z0-9]{12,}/i.test(serialized)) {
    context.addIssue({ code: 'custom', message: 'MANIFEST_MUST_NOT_CONTAIN_PROJECT_SECRETS' })
  }
})

export type PostHogResourceManifest = z.infer<typeof PostHogResourceManifestSchema>

function isSensitivePropertyKey(key: string): boolean {
  return SENSITIVE_PROPERTY_KEY.test(key) && SAFE_ANALYTICS_PROPERTY_KEYS[key] !== true
}

function containsSensitiveValue(value: unknown): boolean {
  if (typeof value === 'string') return EMAIL_VALUE.test(value) || SECRET_VALUE.test(value) || SENSITIVE_URL_VALUE.test(value)
  if (Array.isArray(value)) return value.some((item) => containsSensitiveValue(item))
  if (value && typeof value === 'object') {
    return Object.entries(value).some(([key, child]) => SENSITIVE_PROPERTY_KEY.test(key) || containsSensitiveValue(child))
  }
  return false
}

function isJsonPrimitiveValue(value: unknown): boolean {
  if (value === null) return true
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return true
  if (Array.isArray(value)) return value.every((item) => item === null || typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean')
  return false
}
